import { NgClass } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { DEFAULT_INITIAL_DATE } from '../../../../utils/dates';
import { MatIcon } from '@angular/material/icon';
import { EditorDialogService } from '../../../shared/services/editor-dialog.service';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { hasValidGroupPermission } from '../../../../utils/hasValidGroupPermission';
import { extractColorProperties, getColors, getTextColorsPropertiesStyle } from '../../../../utils/colors';

interface ApprovalItemProperties {
  text: string;
  style: string;
  groupItemIds: string[];
  groupItems: string[];
  acceptedDate: string;
  rejectedDate: string;
  acceptCaption: string;
  rejectCaption: string;
  approverID: string;
  approverName: string;
  rejectReason: string;
  afterProcessed: string;
}

@Component({
  selector: 'lfe-approval-item-form',
  imports: [FormsModule, ReactiveFormsModule, MatIcon, NgClass],
  templateUrl: './approval-item-form.component.html',
  standalone: true,
  styleUrl: './approval-item-form.component.css'
})
export class ApprovalItemFormComponent
  extends InteractiveItemComponent<ApprovalItemProperties>
  implements OnInit, OnDestroy
{
  private readonly editorDialogService = inject(EditorDialogService);
  private readonly translateService = inject(LiveFormsTranslateLoaderService);
  private valueChangesSubscription?: Subscription;
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & ApprovalItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    groupItems: [],
    groupItemIds: [],
    text: this.translateService.instant('liveforms-editor.approval-item.default-text'),
    style: 'Normal',
    acceptedDate: '',
    rejectedDate: '',
    acceptCaption: this.translateService.instant('liveforms-editor.approval-item.buttons.accept'),
    rejectCaption: this.translateService.instant('liveforms-editor.approval-item.buttons.reject'),
    approverID: '',
    approverName: '',
    rejectReason: '',
    afterProcessed: 'False'
  };

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `approval_${this.currentId()}`);

  currentTextColor = signal('');
  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));

  resultUserSignal = signal('');
  resultTimeSignal = signal<Date>(new Date());
  formValueSignal = signal('');

  signatureInfo = computed(() =>
    this.translateService.instant('liveforms-editor.approval-item.signature-info', {
      value: this.formValueSignal() || '',
      user: this.resultUserSignal(),
      date: this.resultTimeSignal().toLocaleDateString('de', { day: '2-digit', month: '2-digit', year: 'numeric' })
    })
  );

  canSign = computed(() =>
    hasValidGroupPermission({
      properties: this.properties,
      defaultGroupName: this.translateService.instant('liveforms-editor.common.default-group-name'),
      userGroups: this.user().userGroups
    })
  );

  /**
   * An `AfterProcessed` approval is only given once the order has been completed.
   *
   * The desktop blocks it while `OrderState != Processed` (`ApprovalItem.SetApproveStates`). Web can
   * only ever edit `Released`, `Paused` and `InProcess` orders (`OrderStateSets.Editable`), so that
   * condition always holds here and the item stays blocked for the whole lifetime of the editor.
   */
  isAfterProcessed(): boolean {
    return this.properties.afterProcessed === 'True';
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    // Initialize signals with current property values
    this.formValueSignal.set(initialValue);
    this.resultUserSignal.set(this.properties.resultUser);

    // Subscribe to form control changes and update the signal
    this.valueChangesSubscription = this.formControl.valueChanges.subscribe((value) => {
      this.formValueSignal.set(value || '');
    });
  }

  override ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & ApprovalItemProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'] || '';
    const id = props['ID'] || '';
    const text = props['Text'] || this.translateService.instant('liveforms-editor.approval-item.default-text');
    const groupItemIds = props['GroupItemIDs']?.split(';') || [];
    const groupItems = props['GroupItems']?.split(';') || [
      this.translateService.instant('liveforms-editor.common.default-group-name')
    ];
    const acceptedDate = props['AcceptedDate'] || '';
    const rejectedDate = props['RejectedDate'] || '';
    const acceptCaption =
      props['AcceptCaption'] || this.translateService.instant('liveforms-editor.approval-item.buttons.accept');
    const rejectCaption =
      props['RejectCaption'] || this.translateService.instant('liveforms-editor.approval-item.buttons.reject');
    const approverID = props['ApproverID'] || '';
    const approverName = props['ApproverName'] || '';
    const rejectReason = props['RejectReason'] || '';
    const afterProcessed = props['AfterProcessed'] || 'False';
    const style = props['Style'] || 'Normal';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));
    this.currentTextColor.set(textColor);

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      groupItemIds,
      groupItems,
      resultValue,
      resultUser,
      resultTime,
      text,
      style,
      acceptedDate,
      rejectedDate,
      acceptCaption,
      rejectCaption,
      approverID,
      approverName,
      rejectReason,
      afterProcessed,
      textBgColor,
      isGenericRepeatItem
    };
  }

  onAcceptClick(): void {
    const now = new Date();
    this.properties.acceptedDate = now.toISOString();
    this.properties.rejectedDate = DEFAULT_INITIAL_DATE;

    this.formControl?.setValue(this.properties.acceptCaption);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.formValueSignal.set(this.properties.acceptCaption);

    // Update user signal (form value signal will be updated by valueChanges subscription)
    this.resultUserSignal.set(this.user().displayName);
    this.resultTimeSignal.set(now);

    this.properties.resultUser = this.user().displayName;
    this.properties.resultValue = this.signatureInfo();
    this.properties.resultTime = DEFAULT_INITIAL_DATE;
    this.properties.approverID = this.user().userID;
    this.properties.approverName = this.user().displayName;
  }

  onRejectClick(): void {
    const now = new Date();
    this.properties.rejectedDate = now.toISOString();
    this.properties.acceptedDate = DEFAULT_INITIAL_DATE;

    this.formControl?.setValue(this.properties.rejectCaption);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.formValueSignal.set(this.properties.rejectCaption);

    // Update user signal (form value signal will be updated by valueChanges subscription)
    this.resultUserSignal.set(this.user().displayName);
    this.resultTimeSignal.set(now);

    this.properties.resultUser = this.user().displayName;
    this.properties.resultValue = this.signatureInfo();
    this.properties.resultTime = DEFAULT_INITIAL_DATE; // TODO: ask Tino or Stefan
    this.properties.approverID = this.user().userID;
    this.properties.approverName = this.user().displayName;
  }

  afterProcessedHint(): string {
    return this.translateService.instant('liveforms-editor.approval-item.after-processed.hint');
  }

  async showAfterProcessedInfo(): Promise<void> {
    await this.editorDialogService.showUserPrompt({
      title: this.afterProcessedHint(),
      message: this.translateService.instant('liveforms-editor.approval-item.after-processed.explanation'),
      icon: 'info',
      iconColor: '#1976d2',
      allowClickOutside: true,
      actions: [
        {
          text: this.translateService.instant('liveforms-editor.common.buttons.ok'),
          action: 'ok',
          color: 'primary',
          variant: 'raised'
        }
      ]
    });
  }

  async resetSignature(): Promise<void> {
    const result = await this.editorDialogService.showUserPrompt({
      title: this.translateService.instant('liveforms-editor.approval-item.dialog.reset.title'),
      message: this.translateService.instant('liveforms-editor.approval-item.dialog.reset.message'),
      icon: 'info',
      iconColor: '#1976d2',
      actions: [
        {
          text: this.translateService.instant('liveforms-editor.approval-item.dialog.reset.actions.yes'),
          action: 'confirm',
          color: 'primary',
          variant: 'raised'
        },
        {
          text: this.translateService.instant('liveforms-editor.approval-item.dialog.reset.actions.no'),
          action: 'cancel',
          color: 'primary',
          variant: 'basic'
        },
        {
          text: this.translateService.instant('liveforms-editor.approval-item.dialog.reset.actions.cancel'),
          action: 'abort',
          color: 'primary',
          variant: 'basic'
        }
      ]
    });

    if (result === 'confirm') {
      this.properties.acceptedDate = DEFAULT_INITIAL_DATE;
      this.properties.rejectedDate = DEFAULT_INITIAL_DATE;

      this.formControl?.setValue('');
      this.formControl?.markAsDirty();
      this.formControl?.markAsTouched();

      this.formValueSignal.set('');

      this.properties.resultValue = '';
      this.properties.resultUser = '';

      // Update user signal (form value signal will be updated by valueChanges subscription)
      this.resultUserSignal.set('');
      this.properties.resultTime = '';
      this.properties.approverID = '';
      this.properties.approverName = '';
    }
  }

  isAccepted(): boolean {
    return this.formControl?.value === this.properties.acceptCaption;
  }

  isRejected(): boolean {
    return this.formControl?.value === this.properties.rejectCaption;
  }

  hasValue(): boolean {
    return !!this.formControl?.value;
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.hasValue()) {
      if (this.isAccepted()) {
        upsertChildren(element, {
          AcceptedDate: this.properties.acceptedDate
        });
      }

      if (this.isRejected()) {
        upsertChildren(element, {
          RejectedDate: this.properties.rejectedDate
        });
      }

      upsertChildren(element, {
        ApproverID: this.properties.approverID,
        ApproverName: this.properties.approverName
      });

      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }
}
